// Controls-coverage findings API client. Mirrors the conventions
// of lib/threatModelFindingsApi.ts so the surface stays consistent
// across the resolver-related panels.

import type { ControlsCoverageFindingsResponse } from '@/types/controlsCoverageFindings'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(detail)
  }
  return (await response.json()) as T
}

export function getControlsCoverageFindings(
  projectId: string,
  runId: number,
): Promise<ControlsCoverageFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/controls-coverage-findings`,
  )
}

/**
 * G3.7 "Regenerate" — re-run the Compliance agent to author hazard
 * records citing the missing in-scope control(s), then return the
 * refreshed findings. One LLM call server-side (~1–2 min). ``clauses``
 * empty → every currently-uncited in-scope control.
 */
export function regenerateControlsCoverage(
  projectId: string,
  runId: number,
  clauses: string[] = [],
): Promise<ControlsCoverageFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/regenerate/controls-coverage`,
    { method: 'POST', body: JSON.stringify({ clauses }) },
  )
}
